import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Plus, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">{label}</label>
    {children}
  </div>
);

export default function LeadSourcesPage() {
  const [sources, setSources]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [name, setName]         = useState("");

  const fetchSources = async () => {
    try {
      const { data } = await axios.get(`${API}/lead-sources`, { withCredentials: true });
      setSources(data);
    } catch {
      toast.error("Failed to load lead sources.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSources(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Lead source name is required."); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/lead-sources`, { name: name.trim() }, { withCredentials: true });
      setSources(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Lead source added successfully!");
      setShowAdd(false);
      setName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add lead source.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead source?")) return;
    try {
      await axios.delete(`${API}/lead-sources/${id}`, { withCredentials: true });
      setSources(prev => prev.filter(s => s.id !== id));
      toast.success("Lead source deleted.");
    } catch {
      toast.error("Failed to delete lead source.");
    }
  };

  return (
    <div className="space-y-5" data-testid="lead-sources-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">Lead Sources</h2>
          <p className="text-xs text-[#475569] mt-0.5">{sources.length} lead source{sources.length !== 1 ? "s" : ""} in master data</p>
        </div>
        <Button
          data-testid="add-lead-source-btn"
          onClick={() => { setName(""); setShowAdd(true); }}
          className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]"
        >
          <Plus size={14} className="mr-1.5" />Add Lead Source
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : sources.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Tag size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No lead sources yet. <button onClick={() => setShowAdd(true)} className="text-[#1B2A6B] font-semibold underline">Add your first lead source</button></p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="lead-sources-table">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                  {["#", "Lead Source Name", "Added On", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sources.map((source, idx) => (
                  <tr key={source.id} data-testid={`lead-source-row-${idx}`} className={`border-b border-[#E2E8F0] ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}>
                    <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{idx + 1}</td>
                    <td className="px-4 py-3 font-semibold text-[#0F172A]">{source.name}</td>
                    <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap">{format(new Date(source.created_at), "dd MMM yyyy")}</td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`delete-lead-source-${idx}`}
                        onClick={() => handleDelete(source.id)}
                        className="text-[#CBD5E1] hover:text-red-500 transition-colors p-1"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Lead Source Dialog */}
      <Dialog open={showAdd} onOpenChange={o => { if (!o) setName(""); setShowAdd(o); }}>
        <DialogContent className="max-w-md rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Add New Lead Source</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Add a lead source to track where your leads come from</p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              <FormField label="Lead Source Name *">
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Facebook, Referral, Website"
                  data-testid="lead-source-name-input"
                  className={INPUT_CLS}
                  autoFocus
                />
              </FormField>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button
                type="button"
                onClick={() => { setName(""); setShowAdd(false); }}
                data-testid="lead-source-cancel-btn"
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors"
              >
                Cancel
              </button>
              <Button
                type="submit"
                data-testid="lead-source-save-btn"
                disabled={saving}
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Lead Source"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
